import { useEffect, useState } from "react";
import { PrePostingPackagingType } from "../../Enums/PrePostingPackagingType";
import FloatingInput from "../FloatingInput/FloatingInput";
import Modal from "../common/Modal/Modal";
import Button from "../Button/Button";

type EmitPrePostingModalProps = {
  isOpen: boolean;
  orderNumber: number;
  onClose: () => void;
  onSubmit: (data: {
    packagingType: PrePostingPackagingType;
    weightGrams: number;
    lengthCm: number;
    widthCm: number;
    heightCm: number;
  }) => Promise<void> | void;
};

type FormData = {
  packagingType: PrePostingPackagingType;
  weightGrams: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
};

const EmitPrePostingModal = ({
  isOpen,
  orderNumber,
  onClose,
  onSubmit,
}: EmitPrePostingModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<FormData>({
    packagingType: PrePostingPackagingType.Box,
    weightGrams: "",
    lengthCm: "",
    widthCm: "",
    heightCm: "",
  });

  useEffect(() => {
    if (!isOpen) {
      setForm({ packagingType: PrePostingPackagingType.Box, weightGrams: "", lengthCm: "", widthCm: "", heightCm: "" });
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePackagingTypeChange = (type: PrePostingPackagingType) => {
    setForm((prev) => ({ ...prev, packagingType: type }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        packagingType: form.packagingType,
        weightGrams: Number(form.weightGrams),
        lengthCm: Number(form.lengthCm),
        widthCm: Number(form.widthCm),
        heightCm: Number(form.heightCm),
      });
    } finally {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Criar Pré-Postagem — Pedido #${orderNumber}`}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Tipo de embalagem</p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handlePackagingTypeChange(PrePostingPackagingType.Box)}
              className={`flex-1 border rounded-lg py-3 text-sm font-semibold transition-colors ${
                form.packagingType === PrePostingPackagingType.Box
                  ? "border-primary-600 bg-primary-50 text-primary-700"
                  : "border-gray-300 text-gray-600 hover:border-gray-400"
              }`}
            >
              Caixa
            </button>
            <button
              type="button"
              onClick={() => handlePackagingTypeChange(PrePostingPackagingType.Bag)}
              className={`flex-1 border rounded-lg py-3 text-sm font-semibold transition-colors ${
                form.packagingType === PrePostingPackagingType.Bag
                  ? "border-primary-600 bg-primary-50 text-primary-700"
                  : "border-gray-300 text-gray-600 hover:border-gray-400"
              }`}
            >
              Pacote
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <FloatingInput
            label="Peso total (g)"
            name="weightGrams"
            type="number"
            value={form.weightGrams}
            onChange={handleChange}
            min={1}
            required
          />
          <FloatingInput
            label="Comprimento (cm)"
            name="lengthCm"
            type="number"
            value={form.lengthCm}
            onChange={handleChange}
            min={1}
            required
          />
          <FloatingInput
            label="Largura (cm)"
            name="widthCm"
            type="number"
            value={form.widthCm}
            onChange={handleChange}
            min={1}
            required
          />
          <FloatingInput
            label="Altura (cm)"
            name="heightCm"
            type="number"
            value={form.heightCm}
            onChange={handleChange}
            min={1}
            required
          />
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary"
            type="button"
            onClick={onClose}
            className="flex-1 py-2"
          >
            CANCELAR
          </Button>
          <Button isLoading={isSubmitting}
            type="submit"
            disabled={isSubmitting}
            className="flex-1 py-2"
          >
            {"EMITIR"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EmitPrePostingModal;
